import React, { useState } from 'react';
import axios from 'axios';
import { Phone, Plus, X, GitMerge, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { getApiUrl } from '../../utils/api';

const API_URL = getApiUrl();

// Alternate numbers on a lead. Adding a number that already belongs to another lead
// auto-merges that lead into this one (payments, bookings and rights carry over).
export const AlternatePhones = ({ lead, onChanged }) => {
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const phones = lead?.alternate_phones || [];
  const emails = lead?.alternate_emails || [];

  const add = async () => {
    if (!phone.trim()) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API_URL}/api/leads/${lead.lead_id}/alternate-phone`, { phone: phone.trim() }, { withCredentials: true });
      setPhone('');
      if (res.data.merged) {
        toast.success(`Merged ${res.data.merged_names.filter(Boolean).join(', ') || 'duplicate lead'} into this lead`);
      } else {
        toast.success('Alternate number added');
      }
      onChanged?.(res.data.merged);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not add number');
    } finally { setBusy(false); }
  };

  const remove = async (p) => {
    setBusy(true);
    try {
      await axios.delete(`${API_URL}/api/leads/${lead.lead_id}/alternate-phone`, { params: { phone: p }, withCredentials: true });
      toast.success('Alternate number removed');
      onChanged?.(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not remove number');
    } finally { setBusy(false); }
  };

  return (
    <div className="md:col-span-2 bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid="alternate-phones-section">
      <div className="flex items-center gap-2 mb-2">
        <Phone className="w-4 h-4 text-slate-500" />
        <h4 className="font-semibold text-slate-800 text-sm">Alternate Numbers ({phones.length})</h4>
      </div>
      <p className="text-xs text-slate-500 mb-3 flex items-start gap-1">
        <GitMerge className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        If this number belongs to another lead (e.g. a 2nd account made during payment), that lead is merged into this one automatically.
      </p>
      <div className="flex gap-2 mb-3">
        <input
          type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder="+91 98765 43210" className="input-base flex-1" data-testid="alternate-phone-input"
        />
        <button type="button" onClick={add} disabled={busy || !phone.trim()} className="btn-secondary inline-flex items-center gap-1 text-sm disabled:opacity-50" data-testid="alternate-phone-add-btn">
          <Plus className="w-4 h-4" /> Add
        </button>
      </div>
      {phones.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {phones.map((p) => (
            <span key={p} className="inline-flex items-center gap-1.5 bg-white border border-slate-200 rounded-full pl-3 pr-1.5 py-1 text-sm text-slate-700" data-testid={`alternate-phone-${p.replace(/\D/g, '')}`}>
              {p}
              <button type="button" onClick={() => remove(p)} disabled={busy} className="p-0.5 rounded-full hover:bg-slate-100 text-slate-400 hover:text-red-600" title="Remove" data-testid={`alternate-phone-remove-${p.replace(/\D/g, '')}`}>
                <X className="w-3.5 h-3.5" />
              </button>
            </span>
          ))}
        </div>
      )}
      {emails.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2" data-testid="alternate-emails">
          {emails.map((e) => (
            <span key={e} className="inline-flex items-center gap-1.5 bg-white border border-slate-200 rounded-full px-3 py-1 text-xs text-slate-600">
              <Mail className="w-3 h-3" /> {e}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};
